import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { Check, ChevronDown, X } from "lucide-react";
import { forwardRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface SelectOption {
	value: string | number;
	label: React.ReactNode;
	disabled?: boolean;
}

// Single select onChange handler types
type SingleOnChangeHandler =
	| ((value: string | number, option: SelectOption) => void)
	| ((value: string) => void)
	| ((value: number) => void)
	| React.Dispatch<React.SetStateAction<string>>
	| React.Dispatch<React.SetStateAction<number>>;

// Multi select onChange handler types
type MultiOnChangeHandler =
	| ((value: (string | number)[], options: SelectOption[]) => void)
	| ((value: string[]) => void)
	| React.Dispatch<React.SetStateAction<string[]>>
	| React.Dispatch<React.SetStateAction<(string | number)[]>>;

// Base props shared by both modes
interface BaseSelectProps extends Omit<
	React.HTMLAttributes<HTMLDivElement>,
	"onChange" | "defaultValue"
> {
	options: SelectOption[];
	placeholder?: string;
	disabled?: boolean;
	size?: "small" | "middle" | "large";
	status?: "error" | "warning";
	allowClear?: boolean;
	loading?: boolean;
	showSearch?: boolean;
	filterOption?: boolean | ((input: string, option: SelectOption) => boolean);
	notFoundContent?: React.ReactNode;
}

// Single select props
interface SingleSelectProps extends BaseSelectProps {
	mode?: undefined;
	value?: string | number;
	defaultValue?: string | number;
	onChange?: SingleOnChangeHandler;
}

// Multi select props
interface MultiSelectProps extends BaseSelectProps {
	mode: "multiple" | "tags";
	value?: (string | number)[];
	defaultValue?: (string | number)[];
	onChange?: MultiOnChangeHandler;
}

export type SelectProps = SingleSelectProps | MultiSelectProps;

const Select = forwardRef<HTMLDivElement, SelectProps>((props, ref) => {
	const {
		className,
		options,
		value,
		defaultValue,
		onChange,
		placeholder = "请选择",
		disabled = false,
		size = "middle",
		status,
		allowClear = false,
		loading = false,
		showSearch = false,
		filterOption = true,
		notFoundContent = "暂无数据",
		mode,
		...restProps
	} = props;

	const isMultiple = mode === "multiple" || mode === "tags";
	const [searchValue, setSearchValue] = useState("");

	const sizeStyles = {
		small: "min-h-7 text-sm py-0.5",
		middle: "min-h-9 text-sm py-1.5",
		large: "min-h-11 text-base py-2",
	};

	const statusStyles = {
		error: "border-red-500 focus-within:border-red-500",
		warning: "border-amber-500 focus-within:border-amber-500",
	};

	const filteredOptions =
		showSearch && searchValue
			? options.filter((opt) => {
					if (typeof filterOption === "function") {
						return filterOption(searchValue, opt);
					}
					if (filterOption) {
						return String(opt.label).toLowerCase().includes(searchValue.toLowerCase());
					}
					return true;
				})
			: options;

	const handleSingleChange = (newValue: string | number) => {
		const option = options.find((opt) => opt.value === newValue);
		if (onChange && option) {
			const handler = onChange as (value: string | number, option?: SelectOption) => void;
			handler(newValue, option);
		}
		setSearchValue("");
	};

	const handleMultiChange = (newValues: (string | number)[]) => {
		const selectedOptions = options.filter((opt) => newValues.includes(opt.value));
		if (onChange) {
			(onChange as MultiOnChangeHandler)(newValues as string[], selectedOptions);
		}
		setSearchValue("");
	};

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (isMultiple) {
			if (onChange) {
				(onChange as MultiOnChangeHandler)([], []);
			}
		} else {
			// @ts-expect-error - clear passes undefined for value
			onChange?.(undefined, undefined);
		}
	};

	const handleRemoveTag = (e: React.MouseEvent, val: string | number) => {
		e.stopPropagation();
		const currentValues = (value as (string | number)[]) || [];
		const newValues = currentValues.filter((v) => v !== val);
		const newOptions = options.filter((opt) => newValues.includes(opt.value));
		if (onChange) {
			(onChange as MultiOnChangeHandler)(newValues as string[], newOptions);
		}
	};

	const hasValue = isMultiple
		? ((value as (string | number)[]) || []).length > 0
		: value !== undefined;

	// Multi-select mode
	if (isMultiple) {
		const selectedValues =
			(value as (string | number)[]) || (defaultValue as (string | number)[]) || [];
		const selectedOptions = options.filter((opt) => selectedValues.includes(opt.value));

		return (
			<div ref={ref} className={cn("relative", className)} {...restProps}>
				<Listbox value={selectedValues} onChange={handleMultiChange} disabled={disabled} multiple>
					<ListboxButton
						className={cn(
							"flex items-center justify-between w-full rounded-lg border border-zinc-300 bg-white px-3 cursor-pointer transition-colors",
							"hover:border-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20",
							"dark:bg-zinc-800 dark:border-zinc-600 dark:hover:border-blue-500",
							sizeStyles[size],
							disabled && "bg-zinc-100 cursor-not-allowed dark:bg-zinc-900",
							status && statusStyles[status],
						)}
					>
						<div className="flex-1 flex flex-wrap gap-1 min-w-0">
							{selectedOptions.length > 0 ? (
								selectedOptions.map((opt) => (
									<span
										key={opt.value}
										className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-100 dark:bg-zinc-700 text-sm"
									>
										{opt.label}
										<button
											type="button"
											onClick={(e) => handleRemoveTag(e, opt.value)}
											className="hover:text-red-500"
										>
											<X className="h-3 w-3" />
										</button>
									</span>
								))
							) : (
								<span className="text-zinc-400">{placeholder}</span>
							)}
						</div>
						<div className="flex items-center gap-1 ml-2 shrink-0">
							{allowClear && hasValue && !disabled && (
								<button
									type="button"
									onClick={handleClear}
									className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
								>
									×
								</button>
							)}
							<ChevronDown className="h-4 w-4 text-zinc-400 transition-transform ui-open:rotate-180" />
						</div>
					</ListboxButton>

					<ListboxOptions
						transition
						anchor="bottom start"
						className={cn(
							"z-[120] w-[var(--button-width)] mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg dark:bg-zinc-800 dark:border-zinc-700 max-h-60 overflow-auto p-1",
							"transition duration-100 ease-out",
							"data-[closed]:opacity-0 data-[closed]:scale-95",
						)}
					>
						{loading ? (
							<div className="px-3 py-2 text-center text-zinc-400">加载中...</div>
						) : filteredOptions.length === 0 ? (
							<div className="px-3 py-2 text-center text-zinc-400">{notFoundContent}</div>
						) : (
							filteredOptions.map((option) => (
								<ListboxOption
									key={option.value}
									value={option.value}
									disabled={option.disabled}
									className={cn(
										"flex items-center justify-between px-3 py-2 cursor-pointer rounded-md transition-colors",
										"data-[focus]:bg-zinc-100 dark:data-[focus]:bg-zinc-700",
										"data-[selected]:bg-blue-50 data-[selected]:text-blue-600 dark:data-[selected]:bg-blue-900/30 dark:data-[selected]:text-blue-400",
										"data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
									)}
								>
									<span>{option.label}</span>
									<Check className="h-4 w-4 invisible data-[selected]:visible" />
								</ListboxOption>
							))
						)}
					</ListboxOptions>
				</Listbox>
			</div>
		);
	}

	// Single-select mode
	const selectedValue = (value as string | number) ?? (defaultValue as string | number);
	const selectedOption = options.find((opt) => opt.value === selectedValue);

	return (
		<div ref={ref} className={cn("relative", className)} {...restProps}>
			<Listbox value={selectedValue} onChange={handleSingleChange} disabled={disabled}>
				<ListboxButton
					className={cn(
						"flex items-center justify-between w-full rounded-lg border border-zinc-300 bg-white px-3 cursor-pointer transition-colors",
						"hover:border-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20",
						"dark:bg-zinc-800 dark:border-zinc-600 dark:hover:border-blue-500",
						sizeStyles[size],
						disabled && "bg-zinc-100 cursor-not-allowed dark:bg-zinc-900",
						status && statusStyles[status],
					)}
				>
					<div className="flex-1 min-w-0">
						{selectedOption ? (
							<span className="text-zinc-900 dark:text-zinc-100 truncate block">
								{selectedOption.label}
							</span>
						) : (
							<span className="text-zinc-400">{placeholder}</span>
						)}
					</div>
					<div className="flex items-center gap-1 ml-2 shrink-0">
						{allowClear && hasValue && !disabled && (
							<button
								type="button"
								onClick={handleClear}
								className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
							>
								×
							</button>
						)}
						<ChevronDown className="h-4 w-4 text-zinc-400 transition-transform ui-open:rotate-180" />
					</div>
				</ListboxButton>

				<ListboxOptions
					transition
					anchor="bottom start"
					className={cn(
						"z-[120] w-[var(--button-width)] mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg dark:bg-zinc-800 dark:border-zinc-700 max-h-60 overflow-auto p-1",
						"transition duration-100 ease-out",
						"data-[closed]:opacity-0 data-[closed]:scale-95",
					)}
				>
					{loading ? (
						<div className="px-3 py-2 text-center text-zinc-400">加载中...</div>
					) : filteredOptions.length === 0 ? (
						<div className="px-3 py-2 text-center text-zinc-400">{notFoundContent}</div>
					) : (
						filteredOptions.map((option) => (
							<ListboxOption
								key={option.value}
								value={option.value}
								disabled={option.disabled}
								className={cn(
									"flex items-center justify-between px-3 py-2 cursor-pointer rounded-md transition-colors",
									"data-[focus]:bg-zinc-100 dark:data-[focus]:bg-zinc-700",
									"data-[selected]:bg-blue-50 data-[selected]:text-blue-600 dark:data-[selected]:bg-blue-900/30 dark:data-[selected]:text-blue-400",
									"data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
								)}
							>
								<span>{option.label}</span>
								<Check className="h-4 w-4 invisible data-[selected]:visible" />
							</ListboxOption>
						))
					)}
				</ListboxOptions>
			</Listbox>
		</div>
	);
});

Select.displayName = "Select";

export { Select };
